import type { NativeProcessStatsSnapshot } from "@-/dev.improve.haniverse.modules.process-stats";
import type { DiagnosticsResourceSample, DiagnosticsThreadSample } from "#diagnostics-types";

function percentOverInterval(
  cpuSeconds: number,
  previousCpuSeconds: number,
  intervalMs: number,
): number | null {
  if (intervalMs <= 0) {
    return null;
  }
  const percent = (((cpuSeconds - previousCpuSeconds) * 1000) / intervalMs) * 100;
  return Number.isFinite(percent) ? Math.max(0, Math.round(percent * 10) / 10) : null;
}

/**
 * Turns two consecutive native snapshots into the process slice of a resource sample. CPU is
 * the delta of cumulative CPU time over the wall-clock interval, as a percentage of one core;
 * per-thread percentages are matched by thread id (a thread that did not exist in the previous
 * snapshot gets `null`, not a wild number).
 */
export function deriveProcessStatsSample(
  current: NativeProcessStatsSnapshot,
  previous: NativeProcessStatsSnapshot | null,
): NonNullable<DiagnosticsResourceSample["process"]> {
  const intervalMs = previous === null ? 0 : current.timestampMs - previous.timestampMs;
  const previousThreads = new Map(
    (previous?.threads ?? []).map((thread) => [thread.id, thread] as const),
  );
  const threads: DiagnosticsThreadSample[] = current.threads.map((thread) => {
    const before = previousThreads.get(thread.id);
    return {
      cpuPercent:
        before === undefined
          ? null
          : percentOverInterval(thread.cpuTimeSeconds, before.cpuTimeSeconds, intervalMs),
      id: thread.id,
      instantCpuPercent:
        thread.instantCpuPercent === null ? null : Math.round(thread.instantCpuPercent * 10) / 10,
      name: thread.name,
      state: thread.state,
    };
  });
  return {
    batteryLevel: current.batteryLevel,
    batteryState: current.batteryState,
    cpuCoreCount: current.cpuCoreCount,
    cpuPercent:
      previous === null
        ? null
        : percentOverInterval(current.cpuTimeSeconds, previous.cpuTimeSeconds, intervalMs),
    diskAvailableBytes: current.diskAvailableBytes,
    isLowMemory: current.isLowMemory,
    isLowPowerModeEnabled: current.isLowPowerModeEnabled,
    javaHeapMaxBytes: current.javaHeapMaxBytes,
    javaHeapUsedBytes: current.javaHeapUsedBytes,
    mainThreadLatencyMs: current.mainThreadLatencyMs,
    mainThreadMaxLatencyMs: current.mainThreadMaxLatencyMs,
    mainThreadStallCount: current.mainThreadStallCount,
    memoryAvailableBytes: current.memoryAvailableBytes,
    memoryFootprintBytes: current.memoryFootprintBytes,
    memoryPeakFootprintBytes: current.memoryPeakFootprintBytes,
    memoryResidentBytes: current.memoryResidentBytes,
    nativeHeapAllocatedBytes: current.nativeHeapAllocatedBytes,
    thermalState: current.thermalState,
    threadCount: current.threadCount,
    threads,
  };
}
